const STORAGE_PREFIX = 'analysis_execution';
const NOTIFICATION_DISMISS_PREFIX = 'analysis_notification_dismiss';
const GLOBAL_STORAGE_KEY = `${STORAGE_PREFIX}:global`;
// Защитный TTL для зависших состояний "running" (30 минут).
const RUNNING_STATE_TTL_MS = 30 * 60 * 1000;

type ExecutionStorageState =
  | {
      status: 'running';
      draftText: string;
      updatedAt: number;
    }
  | {
      status: 'completed';
      analysisResultId: number | null;
      resultText?: string;
      analysisTitle?: string;
      updatedAt: number;
    }
  | {
      status: 'error';
      updatedAt: number;
    };

type ExecutionStorageKeyPayload = {
  organizationId: string;
  documentId: string;
  analysisTypeId: number;
};

function makeStorageKey(organizationId: string, documentId: string, analysisTypeId: number): string {
  return `${STORAGE_PREFIX}:${organizationId}:${documentId}:${analysisTypeId}`;
}

function makeNotificationDismissKey(organizationId: string, documentId: string, analysisTypeId: number): string {
  return `${NOTIFICATION_DISMISS_PREFIX}:${organizationId}:${documentId}:${analysisTypeId}`;
}

function dispatchStorageEvent(key: string, newValue: string | null, oldValue: string | null): void {
  try {
    const event = new StorageEvent('storage', {
      key,
      newValue,
      oldValue,
      storageArea: window.localStorage,
      url: window.location.href,
    });

    window.dispatchEvent(event);
  } catch {
    /* no-op */
  }
}

function isStaleRunning(updatedAt: number): boolean {
  return Date.now() - updatedAt > RUNNING_STATE_TTL_MS;
}

export function parseExecutionStorageKey(key: string | null): ExecutionStorageKeyPayload | null {
  if (!key || !key.startsWith(`${STORAGE_PREFIX}:`)) {
    return null;
  }

  const [, organizationId, documentId, analysisTypeIdRaw] = key.split(':');
  const analysisTypeId = Number(analysisTypeIdRaw);

  if (!organizationId || !documentId || !Number.isFinite(analysisTypeId)) {
    return null;
  }

  return { organizationId, documentId, analysisTypeId };
}

function parseNotificationDismissKey(key: string | null): ExecutionStorageKeyPayload | null {
  if (!key || !key.startsWith(`${NOTIFICATION_DISMISS_PREFIX}:`)) {
    return null;
  }

  const [, organizationId, documentId, analysisTypeIdRaw] = key.split(':');
  const analysisTypeId = Number(analysisTypeIdRaw);

  if (!organizationId || !documentId || !Number.isFinite(analysisTypeId)) {
    return null;
  }

  return { organizationId, documentId, analysisTypeId };
}

function parseState(raw: string | null): ExecutionStorageState | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as ExecutionStorageState | null;

    if (!parsed || typeof parsed !== 'object' || typeof parsed.status !== 'string') {
      return null;
    }

    if (parsed.status === 'running' && typeof parsed.updatedAt === 'number' && isStaleRunning(parsed.updatedAt)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function readExecutionStorageState(
  organizationId: string,
  documentId: string,
  analysisTypeId: number,
): ExecutionStorageState | null {
  const key = makeStorageKey(organizationId, documentId, analysisTypeId);
  const parsed = parseState(window.localStorage.getItem(key));

  if (parsed?.status === 'running' && parsed.updatedAt && isStaleRunning(parsed.updatedAt)) {
    removeExecutionStorageState(organizationId, documentId, analysisTypeId);

    return null;
  }

  return parsed;
}

export function writeExecutionStorageState(
  organizationId: string,
  documentId: string,
  analysisTypeId: number,
  state: ExecutionStorageState,
): void {
  const key = makeStorageKey(organizationId, documentId, analysisTypeId);
  const oldValue = window.localStorage.getItem(key);
  const newValue = JSON.stringify(state);

  window.localStorage.setItem(key, newValue);
  dispatchStorageEvent(key, newValue, oldValue);
}

export function removeExecutionStorageState(organizationId: string, documentId: string, analysisTypeId: number): void {
  const key = makeStorageKey(organizationId, documentId, analysisTypeId);
  const oldValue = window.localStorage.getItem(key);

  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null, oldValue);
}

export function subscribeExecutionStorage(
  organizationId: string,
  documentId: string,
  analysisTypeId: number,
  listener: (state: ExecutionStorageState | null) => void,
): () => void {
  const key = makeStorageKey(organizationId, documentId, analysisTypeId);

  const handler = (event: StorageEvent) => {
    if (event.key !== key) {
      return;
    }

    listener(parseState(event.newValue));
  };

  window.addEventListener('storage', handler);

  return () => {
    window.removeEventListener('storage', handler);
  };
}

export function subscribeAnyExecutionStorage(
  listener: (params: { key: ExecutionStorageKeyPayload; state: ExecutionStorageState | null }) => void,
): () => void {
  const handler = (event: StorageEvent) => {
    const parsedKey = parseExecutionStorageKey(event.key);

    if (!parsedKey) {
      return;
    }

    listener({
      key: parsedKey,
      state: parseState(event.newValue),
    });
  };

  window.addEventListener('storage', handler);

  return () => {
    window.removeEventListener('storage', handler);
  };
}

type GlobalExecutionState = {
  status: 'running';
  organizationId: string;
  documentId: string;
  analysisTypeId: number;
  updatedAt: number;
};

function parseGlobalState(raw: string | null): GlobalExecutionState | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as GlobalExecutionState | null;

    if (!parsed || parsed.status !== 'running') {
      return null;
    }

    if (typeof parsed.updatedAt === 'number' && isStaleRunning(parsed.updatedAt)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function readGlobalExecutionState(): GlobalExecutionState | null {
  const parsed = parseGlobalState(window.localStorage.getItem(GLOBAL_STORAGE_KEY));

  if (parsed?.updatedAt && isStaleRunning(parsed.updatedAt)) {
    removeGlobalExecutionState();

    return null;
  }

  return parsed;
}

export function writeGlobalExecutionState(state: GlobalExecutionState): void {
  const oldValue = window.localStorage.getItem(GLOBAL_STORAGE_KEY);
  const newValue = JSON.stringify(state);

  window.localStorage.setItem(GLOBAL_STORAGE_KEY, newValue);
  dispatchStorageEvent(GLOBAL_STORAGE_KEY, newValue, oldValue);
}

export function removeGlobalExecutionState(): void {
  const oldValue = window.localStorage.getItem(GLOBAL_STORAGE_KEY);

  window.localStorage.removeItem(GLOBAL_STORAGE_KEY);
  dispatchStorageEvent(GLOBAL_STORAGE_KEY, null, oldValue);
}

export function subscribeGlobalExecution(listener: (state: GlobalExecutionState | null) => void): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key !== GLOBAL_STORAGE_KEY) {
      return;
    }

    listener(parseGlobalState(event.newValue));
  };

  window.addEventListener('storage', handler);

  return () => {
    window.removeEventListener('storage', handler);
  };
}

export function publishNotificationDismiss(params: ExecutionStorageKeyPayload): void {
  const key = makeNotificationDismissKey(params.organizationId, params.documentId, params.analysisTypeId);
  const oldValue = window.localStorage.getItem(key);

  // Значение не важно, главное — триггернуть storage-событие в других вкладках.
  const newValue = String(Date.now());

  window.localStorage.setItem(key, newValue);
  dispatchStorageEvent(key, newValue, oldValue);
}

export function subscribeNotificationDismiss(listener: (key: ExecutionStorageKeyPayload) => void): () => void {
  const handler = (event: StorageEvent) => {
    if (!event.newValue) {
      return;
    }

    const parsedKey = parseNotificationDismissKey(event.key);

    if (!parsedKey) {
      return;
    }

    listener(parsedKey);
  };

  window.addEventListener('storage', handler);

  return () => {
    window.removeEventListener('storage', handler);
  };
}

const dismissSyncIds = new Set<string>();

export function runWithDismissSyncGuard(notificationId: string, action: () => void): void {
  dismissSyncIds.add(notificationId);
  try {
    action();
  } finally {
    dismissSyncIds.delete(notificationId);
  }
}

export function shouldSkipDismissBroadcast(notificationId: string): boolean {
  return dismissSyncIds.has(notificationId);
}

export type { ExecutionStorageState, ExecutionStorageKeyPayload, GlobalExecutionState };
